import FontFaceObserver from 'fontfaceobserver';

const ARABIC_FONT_REGEX = /arabic|arab|amiri|noto.*arab|kacst|traditional\s*arabic/i;

export const isArabicFont = (fontFamily) => {
  return fontFamily && typeof fontFamily === 'string' && ARABIC_FONT_REGEX.test(fontFamily);
};

export const getFontTestString = (fontFamily) => {
  if (!fontFamily || typeof fontFamily !== 'string') return null;
  if (ARABIC_FONT_REGEX.test(fontFamily)) {
    return 'مرحبا';
  }
  if (/chinese|han|noto.*cjk|source\s*han/i.test(fontFamily)) {
    return '中国';
  }
  return null;
};

export function getRuntimeFontFamily(font) {
  return (
    font?.family ||
    font?.name?.replace(/\.(ttf|otf|woff2?|eot)$/i, '') ||
    ''
  );
}

export function detectFontFormat(font) {
  if (font.font_format) return font.font_format;
  const url = (font.font || font.name || '').toLowerCase();
  if (url.endsWith('.woff2')) return 'woff2';
  if (url.endsWith('.woff')) return 'woff';
  if (url.endsWith('.otf')) return 'opentype';
  if (url.endsWith('.ttf')) return 'truetype';
  if (url.endsWith('.eot')) return 'embedded-opentype';
  return 'truetype';
}

const loadedFontUrls = new Set();

export async function loadBrowserFont(font) {
  const family = getRuntimeFontFamily(font);
  const url = font?.font;
  if (!family || !url) return false;

  // Track by exact file URL, not by family: many uploaded fonts share the same
  // `family` (e.g. several "Pragmatica" files), so a family-only check would
  // mark the rest as loaded without ever fetching their actual file.
  if (loadedFontUrls.has(url)) return true;

  const spec = `16px "${family}"`;
  const format = detectFontFormat(font);

  try {
    const face = new FontFace(
      family,
      `url("${url}") format("${format}")`,
      { display: 'swap' },
    );
    const loadedFace = await face.load();
    document.fonts.add(loadedFace);
    loadedFontUrls.add(url);
    return true;
  } catch (e) {
    // FontFace.load() can fail on CORS / network. Fall back to the CSS
    // @font-face rules injected by AppGlobalFonts via document.fonts.load.
    try {
      await loadFontFamily(family, 10000);
      const ok = document.fonts.check(spec);
      if (ok) loadedFontUrls.add(url);
      else console.warn('Font not applied (fallback):', family, url);
      return ok;
    } catch (e2) {
      console.warn('Font failed to load:', family, url, e2);
      return false;
    }
  }
}

/**
 * Resolve once a font family is actually usable.
 *
 * Uses the native CSS Font Loading API (`document.fonts`) first, falling back to
 * FontFaceObserver only when the API is unavailable. FontFaceObserver's
 * width-measurement heuristic produces false 10s/30s timeouts in some browsers
 * (e.g. Chrome 149) when the page sets a global `font-family`, even though the
 * font is fully loaded — so we never gate rendering on it when the native API
 * can answer the question directly.
 */
export const loadFontFamily = (family, timeout = 10000) => {
  if (!family || typeof family !== 'string') return Promise.resolve();
  const testString = getFontTestString(family);
  const spec = `16px "${family}"`;

  if (typeof document !== 'undefined' && document.fonts && document.fonts.load) {
    try {
      if (document.fonts.check(spec)) return Promise.resolve();
    } catch (e) {
      /* invalid spec / not ready — fall through to load() */
    }
    return document.fonts.load(spec, testString || 'BESbswy').then(() => {
      try {
        if (!document.fonts.check(spec)) {
          throw new Error(`Font not applied: ${family}`);
        }
      } catch (e) {
        throw new Error(`Font not applied: ${family}`);
      }
    });
  }

  return new FontFaceObserver(family).load(testString, timeout);
};

class FontsService {
  loadedFonts = [];

  loadFonts = async (fonts) => {
    if (!fonts || !fonts.length) {
      return;
    }

    // Load in small batches so we don't fire ~70 parallel requests at the font
    // storage host at once (which can cause sporadic failures / throttling).
    const BATCH_SIZE = 8;
    const failed = [];

    for (let i = 0; i < fonts.length; i += BATCH_SIZE) {
      const batch = fonts.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async (font) => {
          const ok = await loadBrowserFont(font);
          if (ok) {
            this.loadedFonts.push(font.id);
          } else {
            failed.push(font);
          }
          return ok;
        }),
      );

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failed.push(batch[index]);
          console.warn('Font failed:', batch[index], result.reason);
        }
      });
    }

    if (failed.length) {
      console.warn(
        `Fonts not applied: ${failed.length}/${fonts.length}`,
        failed.map((f) => ({ id: f.id, family: f.family, font: f.font })),
      );
    }
  };
}

export const fontsService = new FontsService();
